import { useState, useEffect, useRef } from "react";

function generateMonths() {
  const months = [];
  const start = new Date(2024, 0);
  const end = new Date();
  end.setMonth(end.getMonth() + 3);
  const current = new Date(start);
  while (current <= end) {
    months.push(
      `${current.toLocaleString("default", { month: "short" })} ${current.getFullYear()}`
    );
    current.setMonth(current.getMonth() + 1);
  }
  return months;
}

const ALL_MONTHS = generateMonths();
const PAGE_SIZE = 12;

function getCurrentMonthIndex() {
  const now = new Date();
  const label = `${now.toLocaleString("default", { month: "short" })} ${now.getFullYear()}`;
  return ALL_MONTHS.indexOf(label);
}

export default function MonthSelectorPaginated({ selectedMonths, setSelectedMonths }) {
  const currentMonthIndex = getCurrentMonthIndex();
  const initialPage = Math.floor(currentMonthIndex / PAGE_SIZE);

  const [page, setPage] = useState(initialPage);
  const [displayPage, setDisplayPage] = useState(initialPage);
  const [slideDir, setSlideDir] = useState(null); // 'left' | 'right' | null
  const [animating, setAnimating] = useState(false);
  const scrollRef = useRef(null);
  const isScrolling = useRef(false);

  const totalPages = Math.ceil(ALL_MONTHS.length / PAGE_SIZE);
  const pageMonths = ALL_MONTHS.slice(displayPage * PAGE_SIZE, (displayPage + 1) * PAGE_SIZE);

  const goTo = (nextPage, dir) => {
    if (animating) return;
    if (nextPage < 0 || nextPage >= totalPages) return;
    setSlideDir(dir);
    setAnimating(true);
    setPage(nextPage);
  };

  // When page changes, run the animation then swap displayPage
  useEffect(() => {
    if (page === displayPage) return;
    const timeout = setTimeout(() => {
      setDisplayPage(page);
      setSlideDir(null);
      setAnimating(false);
    }, 320);
    return () => clearTimeout(timeout);
  }, [page, displayPage]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      if (isScrolling.current) return;
      const dir = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      if (dir > 0) {
        isScrolling.current = true;
        goTo(page + 1, "left");
        setTimeout(() => (isScrolling.current = false), 500);
      } else if (dir < 0) {
        isScrolling.current = true;
        goTo(page - 1, "right");
        setTimeout(() => (isScrolling.current = false), 500);
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [page, totalPages, animating]);

  const getSlideStyle = () => {
    if (!animating || !slideDir) return { transform: "translateX(0)", opacity: 1, transition: "none" };
    return {
      transform: slideDir === "left" ? "translateX(-60px)" : "translateX(60px)",
      opacity: 0,
      transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease",
    };
  };

  const toggle = (month) => {
    if (selectedMonths?.includes(month)) {
      setSelectedMonths(selectedMonths.filter((m) => m !== month));
    } else {
      setSelectedMonths([...(selectedMonths || []), month]);
    }
  };

  const arrowBtn = (disabled, onClick, points) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 36, height: 36, borderRadius: 10,
        border: "1px solid #e2e8f0", background: "#fff",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, opacity: disabled ? 0.35 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points={points} />
      </svg>
    </button>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>

      {arrowBtn(page === 0 && !animating, () => goTo(page - 1, "right"), "15 18 9 12 15 6")}

      <div
        ref={scrollRef}
        style={{ flex: 1, overflow: "hidden" }}
      >
        <div style={{ display: "flex", gap: "10px", ...getSlideStyle() }}>
          {pageMonths.map((month) => {
            const isSelected = selectedMonths?.includes(month);
            const isCurrentMonth = ALL_MONTHS.indexOf(month) === currentMonthIndex;
            return (
              <button
                key={month}
                onClick={() => toggle(month)}
                style={{
                  padding: "10px 18px",
                  borderRadius: "10px",
                  border: isSelected
                    ? "none"
                    : isCurrentMonth
                    ? "1.5px solid #155DFC"
                    : "1px solid #e2e8f0",
                  backgroundColor: isSelected ? "#155DFC" : "#fff",
                  color: isSelected ? "#fff" : isCurrentMonth ? "#155DFC" : "#1e293b",
                  fontSize: "clamp(13px, 1.4vw, 15px)",
                  fontWeight: isSelected || isCurrentMonth ? "600" : "400",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                }}
              >
                {isSelected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                {month}
              </button>
            );
          })}
        </div>
      </div>

      {arrowBtn(page >= totalPages - 1 && !animating, () => goTo(page + 1, "left"), "9 18 15 12 9 6")}

    </div>
  );
}